import React, { useContext, useRef, useState } from "react";
import { ModalContext, showErrorDialog } from "@SeemplicityDev/shared";
import useLogger from "../../../../Logger/useLogger";
import EditScopeDetails from "./EditScopeDetails";
import { useRenameScope } from "./useScopeGuardrailMutations";
import { collisionMessage } from "./scopeTreeUtils";

export interface EditScopeDetailsModalProps {
  scopeId: string;
  scopeName?: string;
  onRenamed: (name: string) => void;
}

const RENAME_FAILED_MESSAGE = "Couldn't rename this scope. Please try again.";

const EditScopeDetailsModal: React.FC<EditScopeDetailsModalProps> = ({
  scopeId,
  scopeName,
  onRenamed,
}) => {
  const { closeModal } = useContext(ModalContext);
  const { logWarning } = useLogger();
  const { renameScope, loading } = useRenameScope();
  const [name, setName] = useState(scopeName ?? "");
  const [collision, setCollision] = useState<string>();
  // `loading` flips a render late; this blocks a double-click.
  const inFlight = useRef(false);

  const onNameChange = (value: string) => {
    setName(value);
    setCollision(undefined);
  };

  const confirm = () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    const trimmed = name.trim();
    renameScope(scopeId, trimmed)
      .then(({ ok, nameCollisions }) => {
        if (ok) {
          onRenamed(trimmed);
          closeModal();
          return;
        }
        setCollision(collisionMessage(nameCollisions, "scope", trimmed));
      })
      .catch((err: unknown) => {
        // The BE logs the failure; a warning keeps the raw error out of Sentry.
        logWarning(err, "Scope guardrails: rename_finding_scope_v3 failed");
        showErrorDialog({ message: RENAME_FAILED_MESSAGE });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  return (
    <EditScopeDetails
      name={name}
      onNameChange={onNameChange}
      collisionError={collision}
      saveDisabled={loading || name.trim() === (scopeName ?? "")}
      onConfirm={confirm}
      onCancel={closeModal}
    />
  );
};

export default EditScopeDetailsModal;
